import React, { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, ArrowUpRight } from 'lucide-react'
import SEOHead from '../components/SEOHead'
import { scrollToTop } from '../utils/smoothScroll'

const BLOG_JSON_URL = import.meta.env.VITE_BLOG_SERVE_URL || 'https://n8n.planetx.cfd/webhook/blog-posts'

function getSuggestedPosts(currentPost, allPosts, maxResults = 3) {
  const others = allPosts.filter(p => p.id !== currentPost.id && p.status === 'published')
  const byTags = others
    .map(post => ({ post, score: post.tags.filter(t => currentPost.tags.includes(t)).length }))
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(x => x.post)
  const byBacklinks = others.filter(p => currentPost.backlinks?.includes(p.id))
  const byRecency = [...others].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
  const seen = new Set()
  const results = []
  for (const post of [...byTags, ...byBacklinks, ...byRecency]) {
    if (!seen.has(post.id) && results.length < maxResults) {
      seen.add(post.id)
      results.push(post)
    }
  }
  return results
}

function getInitials(name) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function SkeletonLoader() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-600/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>
      <div className="relative z-10">
        <div className="w-full aspect-[21/9] md:aspect-[16/9] bg-white/5 animate-pulse" />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="h-4 w-24 bg-white/10 rounded animate-pulse mb-6" />
          <div className="h-10 w-3/4 bg-white/10 rounded animate-pulse mb-4" />
          <div className="h-10 w-1/2 bg-white/10 rounded animate-pulse mb-8" />
          <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/10">
            <div className="w-10 h-10 rounded-full bg-white/10 animate-pulse" />
            <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="h-4 bg-white/5 rounded animate-pulse" />
            <div className="h-4 bg-white/5 rounded animate-pulse w-5/6" />
            <div className="h-4 bg-white/5 rounded animate-pulse w-4/6" />
            <div className="h-4 bg-white/5 rounded animate-pulse" />
            <div className="h-4 bg-white/5 rounded animate-pulse w-3/4" />
          </div>
        </div>
      </div>
    </div>
  )
}

function BacklinkCard({ post }) {
  return (
    <a
      href={`/blog/${post.slug}`}
      onClick={(e) => { e.preventDefault(); scrollToTop(800); setTimeout(() => { window.location.href = `/blog/${post.slug}` }, 850) }}
      className="snap-start flex-shrink-0 w-72 md:w-auto group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 hover:border-purple-500/40 transition-all duration-300 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.3)]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      {post.coverImage && (
        <div className="relative overflow-hidden">
          <img src={post.coverImage} alt={post.title} className="w-full aspect-[16/9] object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] to-transparent opacity-60" />
        </div>
      )}
      <div className="relative p-5">
        <div className="text-sm font-semibold text-white group-hover:text-purple-400 transition-colors line-clamp-2 mb-2">{post.title}</div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Calendar size={12} />
          {formatDate(post.publishedAt)}
        </div>
      </div>
    </a>
  )
}

function SuggestedCard({ post }) {
  return (
    <a
      href={`/blog/${post.slug}`}
      onClick={(e) => { e.preventDefault(); scrollToTop(800); setTimeout(() => { window.location.href = `/blog/${post.slug}` }, 850) }}
      className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 hover:border-purple-500/40 transition-all duration-300 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.3)]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      {post.coverImage && (
        <div className="relative overflow-hidden rounded-t-2xl">
          <img src={post.coverImage} alt={post.title} className="w-full aspect-[16/9] object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] to-transparent opacity-40" />
        </div>
      )}
      <div className="relative p-5">
        <div className="text-base font-semibold text-white mb-2 line-clamp-2 group-hover:text-purple-400 transition-colors">{post.title}</div>
        {post.excerpt && <div className="text-sm text-gray-400 line-clamp-2 mb-4 leading-relaxed">{post.excerpt}</div>}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {post.tags.slice(0, 2).map(tag => (
              <span key={tag} className="px-2.5 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-xs">{tag}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-white/5">
          <span className="flex items-center gap-1.5"><Calendar size={12} />{formatDate(post.publishedAt)}</span>
          {post.readingTime && <span className="flex items-center gap-1.5"><Clock size={12} />{post.readingTime} min</span>}
        </div>
      </div>
    </a>
  )
}

export default function BlogPost() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [post, setPost] = useState(null)
  const [allPosts, setAllPosts] = useState([])
  const [suggestedPosts, setSuggestedPosts] = useState([])
  const [referencedBy, setReferencedBy] = useState([])
  const [prevPost, setPrevPost] = useState(null)
  const [nextPost, setNextPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function fetchPost() {
      try {
        const res = await fetch(BLOG_JSON_URL)
        if (!res.ok) throw new Error('Failed to fetch')
        const data = await res.json()
        const posts = Array.isArray(data) ? data : (data.posts || [])
        if (cancelled) return
        setAllPosts(posts)
        const found = posts.find(p => p.slug === slug && p.status === 'published')
        if (!found) {
          setError(true)
          setLoading(false)
          return
        }
        setPost(found)
        const suggested = getSuggestedPosts(found, posts)
        setSuggestedPosts(suggested)
        const refBy = posts.filter(p => p.backlinks?.includes(found.id) && p.id !== found.id)
        setReferencedBy(refBy)
        const published = posts.filter(p => p.status === 'published').sort((a, b) => new Date(a.publishedAt) - new Date(b.publishedAt))
        const idx = published.findIndex(p => p.id === found.id)
        if (idx > 0) setPrevPost(published[idx - 1])
        if (idx < published.length - 1) setNextPost(published[idx + 1])
        setLoading(false)
      } catch {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      }
    }
    fetchPost()
    return () => { cancelled = true }
  }, [slug])

  const handleNavClick = (e, targetPath) => {
    e.preventDefault()
    scrollToTop(800)
    setTimeout(() => navigate(targetPath), 850)
  }

  if (loading) return <SkeletonLoader />

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Post Not Found</h1>
          <p className="text-gray-400 mb-8">The blog post you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); scrollToTop(800); setTimeout(() => navigate('/blog'), 850) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:border-purple-500/30 transition-all text-sm"
          >
            <ArrowLeft size={16} />Back to Blog
          </a>
        </div>
      </div>
    )
  }

  const updatedDate = post.updatedAt && post.updatedAt !== post.publishedAt ? post.updatedAt : null

  const coverUrl = post.coverImage || 'https://nodatx.com/og-image.png'
  const coverBase = coverUrl.replace(/\?.*/, '')

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": [
      `${coverBase}?w=1200&h=630&fit=crop`,
      `${coverBase}?w=1200&h=1200&fit=crop`,
      `${coverBase}?w=1200&h=900&fit=crop`
    ],
    "datePublished": post.publishedAt,
    "dateModified": post.updatedAt || post.publishedAt,
    "author": {
      "@type": "Person",
      "name": post.author,
      "url": "https://nodatx.com/#about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Nodatx",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nodatx.com/nodatx.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://nodatx.com/blog/${post.slug}`
    },
    "keywords": post.tags.join(", ")
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://nodatx.com/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://nodatx.com/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://nodatx.com/blog/${post.slug}` }
    ]
  }

  return (
    <>
      <SEOHead
        title={`${post.title} — Nodatx Blog`}
        description={post.excerpt || post.title}
        canonical={`https://nodatx.com/blog/${post.slug}`}
        ogImage={coverUrl}
        ogType="article"
        twitterCard="summary_large_image"
        author={post.author}
        articleMeta={{
          publishedTime: post.publishedAt,
          modifiedTime: post.updatedAt || post.publishedAt,
          author: post.author,
          tag: post.tags
        }}
        jsonLd={[articleSchema, breadcrumbSchema]}
      />
      <div className="min-h-screen bg-[#0a0a0f] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-600/5 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>

        <div className="relative z-10">
          {post.coverImage && (
            <div className="relative w-full aspect-[16/9] md:aspect-[21/9] pt-20">
              <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover" loading="eager" decoding="async" />
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0f] to-transparent" />
            </div>
          )}

          <div className="max-w-4xl mx-auto px-4 py-8">
            <a
              href="/blog"
              onClick={(e) => { e.preventDefault(); scrollToTop(800); setTimeout(() => navigate('/blog'), 850) }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:border-purple-500/30 transition-all text-sm mb-6"
            >
              <ArrowLeft size={16} />Back to Blog
            </a>

            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-gray-500">
                <li><a href="/" className="hover:text-purple-400 transition-colors">Home</a></li>
                <li><span className="text-gray-600">/</span></li>
                <li><a href="/blog" className="hover:text-purple-400 transition-colors">Blog</a></li>
                <li><span className="text-gray-600">/</span></li>
                <li className="text-gray-300 truncate max-w-[200px]">{post.title}</li>
              </ol>
            </nav>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-sm">{tag}</span>
                ))}
              </div>
            )}

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">{post.title}</h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-8 pb-8 border-b border-white/10">
              {post.authorAvatar ? (
                <img src={post.authorAvatar} alt={post.author} className="w-10 h-10 rounded-full object-cover" loading="lazy" decoding="async" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-semibold">{post.author ? getInitials(post.author) : 'N'}</div>
              )}
              {post.author && <span className="text-white font-medium">{post.author}</span>}
              <span className="flex items-center gap-1"><Calendar size={14} />{formatDate(post.publishedAt)}</span>
              {post.readingTime && <span className="flex items-center gap-1"><Clock size={14} />{post.readingTime} min read</span>}
              {updatedDate && <span className="flex items-center gap-1 text-gray-500">Updated {formatDate(updatedDate)}</span>}
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>

          {referencedBy.length > 0 && (
            <div className="max-w-4xl mx-auto px-4 mt-16 pt-8 border-t border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <ArrowUpRight size={16} className="text-purple-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Referenced In</h2>
              </div>
              <p className="text-gray-400 text-sm mb-6 ml-11">Other posts that link to this article</p>
              <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 md:grid md:grid-cols-2 md:overflow-visible">
                {referencedBy.map(p => <BacklinkCard key={p.id} post={p} />)}
              </div>
            </div>
          )}

          {suggestedPosts.length > 0 && (
            <div className="max-w-5xl mx-auto px-4 mt-16 pt-8 border-t border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <div className="w-3 h-0.5 bg-purple-500 rounded-full" />
                </div>
                <h2 className="text-2xl font-bold text-white">Suggested Posts</h2>
              </div>
              <p className="text-gray-400 mb-8 ml-11">More articles you might enjoy</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestedPosts.map(p => <SuggestedCard key={p.id} post={p} />)}
              </div>
            </div>
          )}

          {(prevPost || nextPost) && (
            <div className="max-w-4xl mx-auto px-4 mt-16 pt-8 pb-20 border-t border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {prevPost ? (
                  <a
                    href={`/blog/${prevPost.slug}`}
                    onClick={(e) => handleNavClick(e, `/blog/${prevPost.slug}`)}
                    className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-xl p-5 hover:border-purple-500/30 transition-all group"
                  >
                    <div className="text-xs text-purple-400 font-medium mb-1 flex items-center gap-1">
                      <ArrowLeft size={12} /> Previous Post
                    </div>
                    <div className="text-sm font-semibold text-white group-hover:text-purple-400 transition-colors line-clamp-2">{prevPost.title}</div>
                  </a>
                ) : <div />}
                {nextPost ? (
                  <a
                    href={`/blog/${nextPost.slug}`}
                    onClick={(e) => handleNavClick(e, `/blog/${nextPost.slug}`)}
                    className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-xl p-5 hover:border-purple-500/30 transition-all group md:text-right"
                  >
                    <div className="text-xs text-purple-400 font-medium mb-1 flex items-center gap-1 md:justify-end">
                      Next Post <ArrowLeft size={12} className="rotate-180" />
                    </div>
                    <div className="text-sm font-semibold text-white group-hover:text-purple-400 transition-colors line-clamp-2">{nextPost.title}</div>
                  </a>
                ) : <div />}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
